import React, { useEffect, useState } from 'react';
import { Card, Table, Space, Button, Tag, message, Popover } from 'antd';
import { ReloadOutlined, TeamOutlined, BookOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { AppSettings, LeagueRankingItem, HistoryGranularity } from '../types';
import { leagueAPI, historyAPI } from '../services/api';
import { compareSnapshotsDesc } from '../utils/snapshot';
import HistoryControls from './HistoryControls';

interface Props { settings: AppSettings }

const LeagueRankings: React.FC<Props> = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LeagueRankingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // 历史对比
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [granularity, setGranularity] = useState<HistoryGranularity>('day');
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [snapshotFrom, setSnapshotFrom] = useState<string | undefined>(undefined);
  const [snapshotTo, setSnapshotTo] = useState<string | undefined>(undefined);

  const load = async (p = 1) => {
    setLoading(true);
    try {
      if (historyEnabled && snapshotFrom && snapshotTo) {
  const res = await historyAPI.compare<LeagueRankingItem>({
          category: 'league',
          granularity,
          snapshot_from: snapshotFrom,
          snapshot_to: snapshotTo,
          server_id: settings.serverId ?? undefined,
          page: p,
          page_size: settings.pageSize,
        });
  const items = (res.diffs || []).map((d: any, index: number) => {
          const toItem = d.to || {};
          const fromItem = d.from || {};
          const newRank = (d.new_rank ?? toItem.rank ?? 0) as number;
          const oldRank = (d.old_rank ?? fromItem.rank ?? 0) as number;
          // 分数字段：优先用联盟总战力 league_fightpoint；没有则回退 money；严格同字段相减
          const toScoreField = (toItem.league_fightpoint != null) ? 'league_fightpoint' : (toItem.money != null ? 'money' : null);
          const fromScoreField = (fromItem.league_fightpoint != null) ? 'league_fightpoint' : (fromItem.money != null ? 'money' : null);
          let score_delta = 0;
          if (toScoreField && fromScoreField && toScoreField === fromScoreField) {
            score_delta = (toItem[toScoreField] ?? 0) - (fromItem[fromScoreField] ?? 0);
          } else if (toScoreField) {
            score_delta = (toItem[toScoreField] ?? 0);
          } else if (fromScoreField) {
            score_delta = -(fromItem[fromScoreField] ?? 0);
          }
          const rank_delta = newRank - oldRank;
          // 格式化字段兜底
          const formatted_league_fightpoint = toItem.formatted_league_fightpoint ?? (toItem.league_fightpoint != null ? String(toItem.league_fightpoint) : undefined);
          const formatted_money = toItem.formatted_money ?? (toItem.money != null ? String(toItem.money) : undefined);
    return { ...toItem, rank: newRank, score_delta, rank_delta, formatted_league_fightpoint, formatted_money, league_id: toItem.league_id, is_new: !!d.is_new } as LeagueRankingItem & { score_delta: number; rank_delta: number; is_new?: boolean };
        });
        setData(items as any);
        setTotal(res.total ?? items.length);
        setPage(res.page ?? p);
      } else if (snapshotTo) {
        const res = await historyAPI.getHistory<LeagueRankingItem>({
          category: 'league',
          granularity,
          snapshot: snapshotTo,
          server_id: settings.serverId ?? undefined,
          page: p,
          page_size: settings.pageSize,
        });
        const items = (res.items || []).map((it: any) => ({
          ...it,
          rank: it.rank ?? it.global_rank ?? it.server_rank ?? 0,
          formatted_league_fightpoint: it.formatted_league_fightpoint ?? (it.league_fightpoint != null ? String(it.league_fightpoint) : undefined),
          formatted_money: it.formatted_money ?? (it.money != null ? String(it.money) : undefined),
          league_id: it.league_id,
        }));
        setData(items);
        setTotal(res.total ?? items.length);
        setPage(res.page ?? p);
      } else {
        const res = await leagueAPI.getRankings({
          page: p,
          page_size: settings.pageSize,
          server_id: settings.serverId,
          use_units: settings.useUnits,
        });
        setData(res.items);
        setTotal(res.total);
        setPage(p);
      }
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, [settings.pageSize, settings.serverId, settings.useUnits, historyEnabled, granularity, snapshotFrom, snapshotTo]);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const resp = await historyAPI.listSnapshots({ category: 'league', granularity, limit: 60 });
  const snaps = (resp.snapshots || []).slice().sort(compareSnapshotsDesc);
        setSnapshots(snaps);
        if (historyEnabled) {
          if (snaps.length >= 2) {
            setSnapshotFrom(snaps[1]);
            setSnapshotTo(snaps[0]);
          }
        } else {
          if (snaps.length >= 1) {
            setSnapshotTo(snaps[0]);
          }
        }
      } catch {}
    };
    fetchSnapshots();
  }, [historyEnabled, granularity, settings.serverId]);

  const columns: ColumnsType<any> = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center', render: (v?: number) => <Tag color={v && v<=3 ? 'gold':'blue'} style={{ fontWeight: 'bold' }}>{v ?? '-'}</Tag> },
    { title: '区服', dataIndex: 'server_id', key: 'server_id', width: 80, align: 'center', render: (id?: number) => id != null ? <Tag color="purple">服务器{id}</Tag> : '-' },
    { title: '联盟名', dataIndex: 'name', key: 'name', width: 140, align: 'center', render: name => <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{name}</span> },
    { title: '等级', dataIndex: 'lv', key: 'lv', width: 70, align: 'center', render: (v?: number) => <Tag color="green">{v ?? '-'}</Tag> },
    { title: '总战力', dataIndex: 'formatted_league_fightpoint', key: 'league_fightpoint', width: 120, align: 'center', render: fp => <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>{fp}</span> },
    { title: '人数', dataIndex: 'user_num', key: 'user_num', width: 100, align: 'center', render: (_: any, r) => <span style={{ fontWeight: 'bold' }}>{`${r.user_num ?? '-'} / ${r.num_max ?? '-'}`}</span> },
    { title: '盟主', dataIndex: 'leader_name', key: 'leader_name', width: 120, align: 'center', render: leader => <span style={{ color: '#722ed1' }}>{leader}</span> },
    { title: '资金', dataIndex: 'formatted_money', key: 'money', width: 120, align: 'center', render: money => <span style={{ fontWeight: 'bold', color: '#f5222d' }}>{money}</span> },
    { title: '宣言', dataIndex: 'manifesto', key: 'manifesto', width: 60, align: 'center', render: text => text ? (
        <Popover content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: 240 }}>{text}</div>} trigger="click">
          <BookOutlined style={{ fontSize: 16, color: '#1890ff', cursor: 'pointer' }} />
        </Popover>
      ) : '-' },
    // 对比模式附加列
    ...(historyEnabled ? [
      { title: '总战变化', dataIndex: 'score_delta', key: 'score_delta', width: 110, align: 'center' as const, render: (delta: number) => (
        <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) > 0 ? '#52c41a' : '#f5222d', fontWeight: 500 }}>
          {delta > 0 ? `+${delta}` : `${delta || 0}`}
        </span>
      ) },
      { title: '排名变化', dataIndex: 'rank_delta', key: 'rank_delta', width: 120, align: 'center' as const, render: (delta: number, record: any) => {
        if (record?.is_new) {
          return <Tag color="green" style={{ fontWeight: 700 }}>新上榜</Tag>;
        }
        return (
          <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) < 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}>
            {delta < 0 ? `⬆ ${Math.abs(delta)}` : delta > 0 ? `⬇︎ ${delta}` : '—'}
          </span>
        );
      } },
    ] : []),
  ];

  return (
    <Card 
      title={<Space><TeamOutlined/>联盟排行榜</Space>}
      extra={
        <Space>
          <HistoryControls
            enabled={historyEnabled}
            granularity={granularity}
            snapshots={snapshots}
            from={snapshotFrom}
            to={snapshotTo}
            onChange={(v) => { setHistoryEnabled(v.enabled); setGranularity(v.granularity); setSnapshotFrom(v.from); setSnapshotTo(v.to); }}
          />
          <Button icon={<ReloadOutlined />} onClick={()=>load(page)} loading={loading}>
            刷新
          </Button>
        </Space>
      }
    >
      <Table<LeagueRankingItem>
        columns={columns}
        dataSource={data}
        loading={loading}
  rowKey={(r)=>`${r.server_id}-${r.league_id ?? r.name}`}
        pagination={{ current: page, pageSize: settings.pageSize, total, showTotal: t=>`共 ${t} 条`, onChange: p=>load(p) }}
        scroll={{ x: 900 }}
        size="small"
        bordered
      />
    </Card>
  );
};

export default LeagueRankings;
